import { FoldVertical, UnfoldVertical } from 'lucide-react';
import { useSession } from 'next-auth/react';
import pluralize from 'pluralize';
import { useEffect, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import { Heading } from '@/components/heading';
import { RouteSearchResultRow } from '@/components/modules/route/search-result-row';
import { Text } from '@/components/text';
import { useMarketRates } from '@/hooks/use-market-rates';
import { useTruncated } from '@/hooks/use-truncated';
import { MODE_AIR, MODE_WATER, USER_ACCESS_FEATURES } from '@/lib/constants';
import { RouteModel } from '@/lib/models/route/types';
import { getSegmentMapRoute } from '@/lib/models/route/utils/get-route-map';
import { featuresState } from '@/state/features';
import { locationsState } from '@/state/locations';
import { mapLocationsState } from '@/state/map';
import { selectedRouteState } from '@/state/routes';
import { CargoWeightValue, ContainerSizeType, RouteSortTypes } from '@/types';
import { SegmentModeType } from '@/types/api-types';
import { canAccessFeature } from '@/utils/auth';
import { getCurrentDateWithMinusXDays } from '@/utils/format/date-time';
import { getXenetaAdditionalFields } from '@/utils/rates';
import { getRoutePortPairs } from '@/utils/route';
import { tv } from '@/utils/styles';
import { trackUser } from '@/utils/tracking';

import { RouteNoModeResult } from '../no-mode-result';
import { RouteSearchResultHero } from '../search-result-hero';

export interface RouteListGroupProps {
  groupKey: string;
  mode: SegmentModeType;
  routes: RouteModel[];
  title: string;
  sortingValue: RouteSortTypes;
  isHeroMode?: boolean;
  description?: string;
  containerSize?: ContainerSizeType[];
  cargoWeight?: CargoWeightValue;
  defaultClosed?: boolean;
  onSortingValueChange: (val: RouteSortTypes) => void;
}

const MAX_ROUTES = 3;

const getRoutesToQueryRatesFor = (routes: RouteModel[], numAboveFold: number, areAllVisible: boolean) => {
  const aboveFoldRoutes = routes.slice(0, numAboveFold);
  const belowFoldRoutes = routes.slice(numAboveFold);

  return areAllVisible ? getRoutePortPairs(belowFoldRoutes) : getRoutePortPairs(aboveFoldRoutes);
};

const RouteListGroup = ({
  groupKey,
  mode,
  routes,
  isHeroMode,
  title,
  description,
  containerSize = ['20'],
  cargoWeight,
  defaultClosed = false,
  sortingValue,
  onSortingValueChange,
}: RouteListGroupProps) => {
  const { data: session } = useSession();
  const features = useRecoilValue(featuresState);
  const visibleRoutes = routes.filter((route) => !route.isHidden);
  const shouldHidePanel = defaultClosed || !visibleRoutes.length;
  const setSelectedRoute = useSetRecoilState(selectedRouteState);
  const locations = useRecoilValue(locationsState);
  const setMapLocations = useSetRecoilState(mapLocationsState);

  // Determines if we should pluck the first route off to use as a Hero
  const [firstRoute, ...remainingRoutes] = visibleRoutes;
  const heroRoute = isHeroMode ? firstRoute : null;
  const alternativeRoutes = isHeroMode ? remainingRoutes : visibleRoutes;
  const maxRouteRows = heroRoute ? 2 : MAX_ROUTES;

  // Only show empty placeholder for Ocean and Air results
  const showEmptyPlaceholder = !visibleRoutes.length && (groupKey === MODE_AIR || groupKey === MODE_WATER);

  const { rows, remainingRows, shouldShowLoadMore, shouldShowAllRows, showAllRows } = useTruncated(
    alternativeRoutes,
    maxRouteRows,
  );

  const [isOpen, setIsOpen] = useState<boolean>(!shouldHidePanel);

  const handleDetailClick = (route: RouteModel) => {
    setSelectedRoute(route);
  };

  const handleRouteClick = (route: RouteModel) => {
    setMapLocations(
      getSegmentMapRoute({
        route,
        locationDictionary: locations,
      }),
    );
  };

  const handleShowAllResults = () => {
    showAllRows();
    // Analytics Event
    trackUser.event('Show All Results', { mode: groupKey });
  };

  // Get Market Rates for first three or remaining
  useMarketRates({
    ports: getRoutesToQueryRatesFor(visibleRoutes, MAX_ROUTES, shouldShowAllRows),
    mode,
    dateRange: { dateFrom: getCurrentDateWithMinusXDays(29), dateTo: getCurrentDateWithMinusXDays(1) },
    additionalFields: getXenetaAdditionalFields({ containerSize, cargoWeight, mode }),
  });

  const SHOW_USER_AUTH = !!features.userAuthentication;
  const SHOW_MARKET_RATES = !!features.showMarketRates;
  const isLocked = !canAccessFeature(USER_ACCESS_FEATURES.ROUTE_DETAILS, session, SHOW_USER_AUTH);
  const ratesAreLocked = !SHOW_MARKET_RATES || !canAccessFeature(USER_ACCESS_FEATURES.RATES, session, SHOW_USER_AUTH);

  useEffect(() => {
    setIsOpen(!shouldHidePanel);
  }, [shouldHidePanel]);

  useEffect(() => {
    if (heroRoute) {
      // When HeroRoute is set or changes, update it to be the visible route on the Map
      setMapLocations(
        getSegmentMapRoute({
          route: heroRoute,
          locationDictionary: locations,
        }),
      );
    }
  }, [heroRoute, sortingValue]);

  const { groupHeading, routeList, loadMoreButton, loadMoreBar, loadMoreText } = styles();

  return (
    <>
      {showEmptyPlaceholder && <RouteNoModeResult title={title} />}
      {heroRoute && (
        <RouteSearchResultHero
          route={heroRoute}
          sortValue={sortingValue}
          onDetailsClick={() => handleDetailClick(heroRoute)}
          onRouteClick={() => handleRouteClick(heroRoute)}
          onSortingChange={onSortingValueChange}
          isLocked={isLocked}
          ratesAreLocked={ratesAreLocked}
        />
      )}
      {Boolean(alternativeRoutes.length) && (
        <div className="mb-6" data-cy="route-search-group">
          <div className={groupHeading()}>
            <Heading variant="h4" as="h2" className="mb-0 text-baseSm text-text-primary">
              Alternative {title}
            </Heading>
            {description && (
              <Text variant="sm" className="m-0 italic text-grey-600">
                {alternativeRoutes.length} {description}
              </Text>
            )}
          </div>
          <div className={routeList()}>
            {isOpen &&
              rows.map((route, index: number) => (
                <RouteSearchResultRow
                  onDetailsClick={() => handleDetailClick(route)}
                  onRouteClick={() => handleRouteClick(route)}
                  route={route}
                  key={route.id}
                  index={index}
                  isLocked={isLocked}
                  ratesAreLocked={ratesAreLocked}
                  testId={`route-search-result-item-${title}`}
                />
              ))}
            {!rows.length && (
              <Text variant="secondary" className="m-0 -mt-1 text-text-secondary">
                No {groupKey} routes to show. Try clearing yours filters, or performing a different search.
              </Text>
            )}
            {shouldShowLoadMore && (
              <button type="button" disabled={isLocked} onClick={handleShowAllResults} className={loadMoreButton()}>
                {isLocked ? (
                  <span className={loadMoreText()}>+ {remainingRows} more routes</span>
                ) : (
                  <span className={loadMoreText()}>
                    {shouldShowAllRows ? <FoldVertical className="size-3" /> : <UnfoldVertical className="size-3" />}
                    {shouldShowAllRows ? `Show less` : `${remainingRows} ${pluralize('more route', remainingRows)}`}
                  </span>
                )}
                <span className={loadMoreBar()} />
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
};

const styles = tv({
  slots: {
    groupHeading: 'mb-2',
    routeCount: 'mb-0 ml-2 block rounded bg-white px-1 text-xs text-grey-600',
    routeList: 'grid gap-1',
    loadMoreButton:
      'group relative -mb-2 flex justify-center py-0.5 text-sm transition-none disabled:cursor-not-allowed disabled:text-grey-400',
    loadMoreText:
      'relative z-1 inline-flex items-center justify-center gap-2 rounded bg-grey-100 px-2 py-1 group-hover:bg-white',
    loadMoreBar: 'absolute left-2 right-2 top-3 z-0 border-t border-dashed border-grey-300',
  },
});

export { RouteListGroup };
